<script>
  import { dev } from "$app/env";
  import BlockGridVisualisation from "#lib/BlockGridVisualisation.svelte";
  import * as env from "$app/env/public";

  let {
    children,
    ref = $bindable(),
    class: css_class,
    style,
    /**
    * @type {'full-screen' | 'left-half-screen' | 'right-half-screen'}
    * @default 'full-screen'
    * @description The size of the grid.
    **/
    grid_size = 'full-screen'
  } = $props();

</script>

<div 
  class="grid-container {css_class} grid-size-{grid_size}"
  bind:this={ref}
  {style}
>
  {#if children}
    {@render children()}
  {/if}

  {#if dev && env.PUBLIC_SHOW_BLOCK_GRID_LINES === 'true' && grid_size === 'full-screen'}
    <BlockGridVisualisation {grid_size} />
  {/if}
</div>


<style lang='postcss'>
:root {
  --grid-center-gutter-left: calc(0.5 * var(--grid-gutter));
  --grid-center-gutter-right: calc(0.5 * var(--grid-gutter));

  /*
    24 column grid layout system, with 12x core (inner) columns; 4x extended columns; and 8x flex columns.
    Grid track gutters are dedicated columns for flexible collapsing on small screens.
    Stable core layout from ∞ until 870px optimized for readability.
    Flex columns have no gutters, since elements are not placed side by side here.
  */

  /*
    --track-inner calculates the width of each inner track.
    It uses the smaller value between:
    1. The fixed max characters per line width (dependent on body font-size) divided by 12, minus the average gutter width.
    2. The available viewport width (100vw) minus padding and gutters, divided by 12.
    Ensures inner tracks adjust for both fixed content width and viewport width, aiding fluid typography.
  */

  --track-inner: min(
    calc(var(--max-char-per-line) / 12 - (var(--grid-gutter) * 11 / 12)),
    calc((100% - (var(--page-inline-spacing) * 2) - (var(--grid-gutter) * 11)) / 12)
  );
  @media (min-width: 641px) {   
    /* Problem: When applying viewport width units e.g. 100vw on an HTML element inside of a scrollable web page it causes an horizontal overflow. */
    /* Solution: We need to substract it when using viewport units */
    
    /* Option 1) We're setting --vw in layout via a javascript resize observer. But this causes initial layout shift */
    /* Option 2) We're using container queries: cqw unit and make body a container-type: inline-size */
    /* Read more about this approach here: https://www.smashingmagazine.com/2023/12/new-css-viewport-units-not-solve-classic-scrollbar-problem/#avoiding-the-classic-scrollbar-problem */

    /* Fallback */
    /* 
    Scrollbar width is defined in layout.css, but using the variable --scrollbar-width seems to not recompute, so it stays 0. 
    Instead we calculate it here with (100vw - 100cqw) */
    --track-inner: min(
      calc(var(--max-char-per-line) / 12 - (var(--grid-gutter) * 11 / 12)),
      calc((calc(100vw - (100vw - 100cqw)) - (var(--page-inline-spacing) * 2) - (var(--grid-gutter) * 11)) / 12)
    );
    @supports not (container-type: inline-size) {
      --track-inner: min(
        calc(var(--max-char-per-line) / 12 - (var(--grid-gutter) * 11 / 12)),
        calc((calc(100vw - 16px) - (var(--page-inline-spacing) * 2) - (var(--grid-gutter) * 11)) / 12)
      );
    }
    /* Modern browsers */
    /* @supports (width: 100cqw) {
      --track-inner: min(
        calc(var(--max-char-per-line) / 12 - (var(--grid-gutter) * 11 / 12)),
        calc((100cqw - (var(--page-inline-spacing) * 2) - (var(--grid-gutter) * 11)) / 12)
      );
    } */
  }

  /* IMPORTANT NOTE ⚠️
    Using 100vw instead of 100% does not account for the scrollbar width which is ~15px. 
    This will cause an overflow
  */


  /* These should collapse to zero first */
  /* --track-flex-3: minmax(0, calc(var(--track-inner) + 0.5 * var(--grid-gutter)));
  --track-flex-4: minmax(0, calc(var(--track-inner) + 0.5 * var(--grid-gutter)));
  --track-flex-5: minmax(0, calc(var(--track-inner) + 0.5 * var(--grid-gutter)));
  --track-flex-6: minmax(0, calc(var(--track-inner) + 0.5 * var(--grid-gutter))); */


  /* When the above are fully collapsed, flex should collapse to zero next */
  --track-flex-3: minmax(0, var(--track-inner));
  --track-flex-4: minmax(0, var(--track-inner));
  --track-flex-5: minmax(0, var(--track-inner));
  --track-flex-6: minmax(0, var(--track-inner));

  --track-flex-1: minmax(0, 1fr);
  --track-flex-2: minmax(0, 1fr);
  --track-flex-7: minmax(0, 1fr);
  --track-flex-8: minmax(0, 1fr);


  /* ⚠️ FIRST ISSUE: 
  css doesn't support css variables yet in @media queries. 
  the breakpoint where flex has fully shrinked and extended tracks no longer fits the window 
  is dependent on --track-inner, which itself is dependent on --max-char-per-line, 
  which itself is dependent on the body-font-size
  --> so we cannot represent this dynamic width of the extended spaces as a breakpoint to update the track sizes below

  ⚠️ SECOND ISSUE: 
  We cannot use javascript computations of widths of DOM elements or the viewport, 
  sicne they only run after the page is fully rendered.
  This causes either delays or layout shifts.

  🔴 CURRENT RESOLUTION:
  --track-extended cannot stay the same width like track-innner until there's no space available left,
  but instead like track-flex it needs to shrink gradually.

  🔴 Why is this an issue?
  We cannot render even-sized grid-cards that slot into the grid slots as the extended tracks will shrink dynamically.
  Currently the only alternative would be to not use character based sizing of the inner tracks 
  and use a computed pixel for the media query. But typography will suffer because of this.

  Once env() becomes a well supported feature in CSS above issue can be resolved.
  Until then we need to use different techniques for even sized "card grids". */

  --track-extended: minmax(0,var(--track-inner)); /* Will start collapsing after flex tracks */
  
  --grid-gutter-flex: minmax(0, 1fr);
  --grid-gutter-extended: minmax(0,var(--grid-gutter));
  --grid-gutter-flex-extended: minmax(0, var(--grid-gutter));

  --inner-track-start: 14;
  --inner-track-end: 38;
  --extended-1-track-start: 10;
  --extended-1-track-end: 42;
  --extended-2-track-start: 6;
  --extended-2-track-end: 46;
  --flex-track-start: 2;
  --flex-track-end: 50;
  --center-gutter-track-start: 25;
  --center-gutter-track: 26;
  --center-gutter-track-end: 27;

  /*
    THE INNER GAP of every two-column layout whose media is not full-bleed.

    Columns and gutters alternate in this grid: line 23→24 is a gutter, 24→25 a content column,
    25→27 the centre gutter. So the smallest gap that still lands on the grid's own rhythm is
    one empty slot - gutter + column + centre gutter, ~80px on desktop (an odd offset like -1
    would end a column on the far side of a gutter). The slot always sits LEFT of the centre
    gutter, whatever the two columns hold, so across layouts and their mirrors every left
    column ends on one line and every right column starts on one line: stacked blocks share
    their edges.

    Layouts whose media deliberately fills its half (Carousel Split, Full + Small/Tiny Image,
    covers) do not use these: their gap is the centre gutter, or nothing.
  */
  --split-left-end: 23;
  --split-right-start: var(--center-gutter-track-end);

  /* Grid span definitions (helper utilities) */
  --track-span-full: 1 / -1; /* spans the entire grid */
  --track-span-flex: var(--flex-track-start) / var(--flex-track-end); /* spans the entire flex area */
  --track-span-flex-left: var(--flex-track-start) / var(--center-gutter-track-start); /* spans the entire flex area */
  --track-span-flex-right: var(--center-gutter-track-end) / var(--flex-track-end); /* spans the entire flex area */
  --track-span-left-extended: var(--extended-1-track-start) / var(--center-gutter-track-start); /* spans the left extended area */
  --track-span-right-extended: var(--center-gutter-track-end) / var(--extended-1-track-end); /* spans the right extended area */
  --track-span-extended1: var(--extended-1-track-start) / var(--extended-1-track-end); /* spans the entire extended area */
  --track-span-extended2: var(--extended-2-track-start) / var(--extended-2-track-end); /* spans the entire extended area */
  --track-span-left: 1 / var(--center-gutter-track-start); /* from left to middle */
  --track-span-left-inner: var(--inner-track-start) / var(--center-gutter-track-start); /* left inner content area */
  --track-span-inner: var(--inner-track-start) / var(--inner-track-end); /* covers all inner content area */
  --track-span-right-inner: var(--center-gutter-track-end) / var(--inner-track-end); /* right inner content area */
  --track-span-right: var(--center-gutter-track-end) / -1; /* from middle to right */


  /* template columns */

  --grid-columns-left-flex: 
    /** ------------------------------------------------------------------
    2 - Flex 1 */ 
    var(--track-flex-1) var(--grid-gutter-flex)
    /** ------------------------------------------------------------------
    4 - Flex 2 */ 
    var(--track-flex-2) var(--grid-gutter-flex)
    /** ------------------------------------------------------------------
    6 - Flex 3 */ 
    var(--track-flex-3) var(--grid-gutter-flex-extended)
    /** ------------------------------------------------------------------
    8 - Flex 4 */ 
    var(--track-flex-4) var(--grid-gutter-flex-extended);

  --grid-columns-left-extended: 
    /** ------------------------------------------------------------------
    10 - Extended 1 */ 
    var(--track-extended) var(--grid-gutter-extended)
    /** ------------------------------------------------------------------
    12 - Extended 2 */ 
    var(--track-extended) var(--grid-gutter-extended);

  --grid-columns-left-inner: 
    /** ------------------------------------------------------------------
    14 - Inner 1 */ 
    var(--track-inner) var(--grid-gutter)
    /** ------------------------------------------------------------------
    16 - Inner 2 */ 
    var(--track-inner) var(--grid-gutter)
    /** ------------------------------------------------------------------
    18 - Inner 3 */ 
    var(--track-inner) var(--grid-gutter)
    /** ------------------------------------------------------------------
    20 - Inner 4 */ 
    var(--track-inner) var(--grid-gutter)
    /** ------------------------------------------------------------------
    22 - Inner 5 */ 
    var(--track-inner) var(--grid-gutter)
    /** ------------------------------------------------------------------
    24 - Inner 6 */ 
    var(--track-inner);

  --grid-columns-right-inner:
    /** ------------------------------------------------------------------
    26 - Inner 7 */ 
    var(--track-inner) var(--grid-gutter)
    /** ------------------------------------------------------------------
    28 - Inner 8 */ 
    var(--track-inner) var(--grid-gutter)
    /** ------------------------------------------------------------------
    30 - Inner 9 */ 
    var(--track-inner) var(--grid-gutter)
    /** ------------------------------------------------------------------
    32 - Inner 10 */ 
    var(--track-inner) var(--grid-gutter)
    /** ------------------------------------------------------------------
    34 - Inner 11 */ 
    var(--track-inner) var(--grid-gutter)
    /** ------------------------------------------------------------------
    36 - Inner 12 */ 
    var(--track-inner);

  --grid-columns-right-extended: 
    var(--grid-gutter-extended)
    /** ------------------------------------------------------------------
    38 - Extended 3 */ 
    var(--track-extended) var(--grid-gutter-extended)
    /** ------------------------------------------------------------------
    40 - Extended 4 */ 
    var(--track-extended) var(--grid-gutter-flex-extended)
    /** ------------------------------------------------------------------
    42 - Flex 5 */ 
    var(--track-flex-5) var(--grid-gutter-flex-extended);

  --grid-columns-right-flex:
    /** ------------------------------------------------------------------
    44 - Flex 6 */ 
    var(--track-flex-6) var(--grid-gutter-flex)
    /** ------------------------------------------------------------------
    46 - Flex 7 */ 
    var(--track-flex-7) var(--grid-gutter-flex)
    /** ------------------------------------------------------------------
    48 - Flex 8 */ 
    var(--track-flex-8);
  
  --grid-columns-left-half:   
    var(--grid-columns-left-flex)
    var(--grid-columns-left-extended)
    var(--grid-columns-left-inner);

  --grid-columns-right-half: 
    var(--grid-columns-right-inner)
    var(--grid-columns-right-extended)
    var(--grid-columns-right-flex);

  --grid-columns-full-screen:
    /** 1 Edge (Padding at start) */ 
    var(--page-inline-spacing)
    /** 2-24 Left half */
    var(--grid-columns-left-half)
    /** 25 Center Gutter Left */
    var(--grid-center-gutter-left)
    /** 26 Center Gutter Right */
    var(--grid-center-gutter-right)
    /** 27-49 Right half */
    var(--grid-columns-right-half)
    /** 50 Edge (Padding at end) */ 
    var(--page-inline-spacing);
}

.grid-container {
  position: relative;
  margin-inline: auto;
  width: 100%;
  display: grid;
  &.grid-size-full-screen {
    grid-template-columns: var(--grid-columns-full-screen);
  }
  &.grid-size-left-half-screen {
    grid-template-columns: var(--grid-columns-full-screen);
    @media (min-width: 1025px) {
      grid-template-columns:
        /** 1 Edge (Padding at start) */ 
        var(--page-inline-spacing)
        /** 2-24 Left half */
        var(--grid-columns-left-half)
        /** 25 Center Gutter */
        var(--grid-center-gutter-left)
      ;
    }
  }
  &.grid-size-right-half-screen {
    grid-template-columns: var(--grid-columns-full-screen);
    @media (min-width: 1025px) {
      grid-template-columns:
          /** Center Gutter */ 
        var(--grid-center-gutter-right)
        /** 27-49 Right half */
        var(--grid-columns-right-half)
        /** 50 Edge (Padding at end) */ 
        var(--page-inline-spacing);
    }
  }
}
</style>